import __axios from "axios";
import { axiosV2 } from "config/@axios";
import parseString from "./helpers/parseSearchString";
import * as uiActions from "store/actions/uiActions";
import { normalizeSearchQuery } from "pages/Mail/helpers/searchQuery";

export const FETCH_MESSAGES = "messageActions::fetchMessages";
export const FETCH_MESSAGES_SUCCESS = "messageActions::fetchMessagesSuccess";
export const FETCH_MESSAGES_FAILED = "messageActions::fetchMessagesFailed";
export const FETCH_MESSAGES_APPEND = "messageActions::fetchMessagesAppend";
export const FETCH_MESSAGES_APPEND_SUCCESS =
  "messageActions::fetchMessagesAppendSuccess";
export const FETCH_MESSAGES_APPEND_FAILED =
  "messageActions::fetchMessagesAppendFailed";
export const READ_MESSAGES = "messageActions::readMessages";
export const UNREAD_MESSAGES = "messageActions::unReadMessages";
export const MARK_AS_SPAM = "messageActions::markAsSpam";
export const MARK_AS_UN_SPAM = "messageActions::markAsSpam";
export const TOGGLE_FLAG = "messageActions::toggleFlag";

export const PREPEND_MESSAGE = "messageActions::prependMessage";

const CancelToken = __axios.CancelToken;
let cancel;
let latestRequestId = 0;

export const getIds = messages => {
  const conversationIds = messages
    .filter(item => item.listingType === "conversation")
    .map(item => item.conversation_id || item.id);

  const messageIds = messages
    .filter(item => item.listingType === "message")
    .map(item => item.id);

  const hasConversation = conversationIds.length > 0;
  const hasMessage = messageIds.length > 0;
  const allMessageIds = messages.map(item => item.id);

  return {
    conversationIds,
    messageIds,
    allMessageIds,
    hasConversation,
    hasMessage
  };
};

export const fetchByPage =
  ({ page, folder, silent = false, q = null }) =>
  (dispatch, getState) => {
    const state = getState();
    const perPage = state.auth.settings.display_mail_count || 20;
    let offset;
    if (page < 1) {
      offset = 0;
    } else {
      offset = (page - 1) * perPage;
    }
    const query = normalizeSearchQuery(q) || null;
    const hasCachedQuery = state.ui.paginate.query != null;

    dispatch({
      type: uiActions.CHANGE_PAGE,
      payload: {
        page,
        ...((query !== null || hasCachedQuery) && { query })
      }
    });

    if (folder?.toLowerCase() === "__starred") {
      return dispatch(
        fetchMessages({
          folder,
          offset,
          silent,
          q: query,
          flagged: true,
          isSearch: true
        })
      );
    }

    if (query || folder?.toLowerCase() === "__search") {
      return dispatch(
        fetchMessages({ folder, offset, silent, q: query, isSearch: true })
      );
    }
    return dispatch(fetchMessages({ folder, offset, silent }));
  };

export const fetchMessages =
  ({
    folder = "inbox",
    offset = 0,
    silent = false,
    q = null,
    flagged = false,
    isSearch = false,
    append = false
  }) =>
  (dispatch, getStore) => {
    const state = getStore();
    const listingType =
      folder !== "drafts" ? state.ui.paginate.messageListingType : "message";
    const perPage = state.auth.settings.display_mail_count || 20;
    const unread = state.ui.paginate.unread;
    const sort = state.ui.paginate.sort;
    const requestId = latestRequestId + 1;
    latestRequestId = requestId;
    let requestCancel = null;

    cancel && cancel();

    const fetchType = append ? FETCH_MESSAGES_APPEND : FETCH_MESSAGES;
    const successType = append
      ? FETCH_MESSAGES_APPEND_SUCCESS
      : FETCH_MESSAGES_SUCCESS;
    const failedType = append
      ? FETCH_MESSAGES_APPEND_FAILED
      : FETCH_MESSAGES_FAILED;

    dispatch({
      type: fetchType,
      payload: {
        folder,
        offset,
        silent
      }
    });

    const params = {
      start: offset,
      sort: sort.name,
      sort_type: sort.type,
      count: perPage,
      ...parseString(normalizeSearchQuery(q), true)
    };

    if (unread) {
      params.unread = 1;
    }

    if (flagged) {
      params.flagged = 1;
    }

    if (!isSearch) {
      params.folder = folder.replaceAll(":", "/");
    }

    let query = listingType === "message" ? `/messages` : `/conversations`;
    return axiosV2
      .get(query, {
        params,
        cancelToken: new CancelToken(function executor(e) {
          requestCancel = e;
          cancel = e;
        })
      })
      .then(response => {
        if (requestId !== latestRequestId) {
          return;
        }

        dispatch({
          type: successType,
          payload: {
            folder,
            offset,
            listingType,
            messages: response.data,
            perPage
          }
        });

        if (folder?.toLowerCase() === "inbox" && !isSearch) {
          const readMessages = response.data
            .filter(
              message =>
                message.unread !== undefined &&
                ![true, 1, "1"].includes(message.unread)
            )
            .map(message => ({ ...message, listingType }));

          if (readMessages.length > 0) {
            dispatch(uiActions.inboxNewMessagesHandled(readMessages));
          }
        }
      })
      .catch(err => {
        if (__axios.isCancel(err) || requestId !== latestRequestId) {
          return;
        }

        dispatch({
          type: failedType,
          payload: {
            folder,
            errorText: err.errorMessage
          }
        });
      })
      .finally(() => {
        if (cancel === requestCancel) {
          cancel = null;
        }
      });
  };

export const fetchNextPage =
  ({ page, folder, q = null, silent = false }) =>
  (dispatch, getState) => {
    const state = getState();
    const perPage = state.auth.settings.display_mail_count || 20;
    const offset = (page - 1) * perPage;
    const query = normalizeSearchQuery(q) || null;

    if (folder?.toLowerCase() === "__starred") {
      return dispatch(
        fetchMessages({
          folder,
          offset,
          silent,
          q: query,
          flagged: true,
          isSearch: true,
          append: true
        })
      );
    }

    if (query || folder?.toLowerCase() === "__search") {
      return dispatch(
        fetchMessages({
          folder,
          offset,
          silent,
          q: query,
          isSearch: true,
          append: true
        })
      );
    }

    return dispatch(fetchMessages({ folder, offset, silent, append: true }));
  };

export const prependMessage = message => ({
  type: PREPEND_MESSAGE,
  payload: message
});
